import {
    FIELD_IMPUTATION_LEVEL1,
    FIELD_INVOICE_NUMBER,
    RETIRED_FIELD_EXPENSE_CATEGORY,
    RETIRED_FIELD_IMPUTATION_SUBCATEGORY,
    DEFAULT_REQUIRED_EXPENSE_FIELDS,
    OPT_IN_EXPENSE_FIELDS,
    REVIEW_MODE_DISABLED_EXPENSE_FIELDS,
} from '../components/client-form/expense-field-flags';
import {
    REVIEW_MODE_CLIENT_ONLY,
    REVIEW_MODE_CLIENT_NON_CLIENT,
    REVIEW_MODE_CUSTOM,
} from '../components/client-form/review-seed-presets';
import { dropdownWidthStyle } from './dropdown-width';
import { resolveExpenseFieldOrder } from './expense-field-order';

export default class Utils {
    static getSelectInputFontSize() {
        // iOS Safari zooms focused inputs when text is smaller than 16px.
        // Apply 16px only on mobile-sized viewports and keep desktop size unchanged.
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
            return "0.875rem";
        }
        return window.matchMedia("(max-width: 767px)").matches ? "16px" : "0.875rem";
    }

    static userHasPageAccess(data, page) {
        return data != null && data.config != null && data.config.components != null && data.config.components.findIndex(comp => comp.name === page) !== -1;
    }

    static userHasPageOptionAccess(data, moduleName, optionName) {
        if (!this.userHasPageAccess(data, moduleName)) {
            return false;
        }
        //options is an array of objects with name property
        const pageModule = data.config.components.find(comp => comp.name === moduleName);
        return pageModule?.options?.findIndex(option => option.name === optionName) !== -1;
    }

    /**
     * Returns true if the given expense field flag is active for the client.
     *
     * When no `field_*` options have been seeded yet (legacy clients), the method
     * returns `defaultValue` so all existing forms keep working without change.
     *
     * Use `defaultValue = true`  for visibility flags (show/hide a field).
     * Use `defaultValue = false` for opt-in behaviour flags (e.g. invoice date editable
     * and `field_imputation_level1`).
     */
    /**
     * Whether the tenant may create suppliers from Xpens (XPEN-3267).
     *
     * Mirrors `client_allows_supplier_create` on the API: only a literal `false`
     * blocks, so a client whose config carries no `suppliers` block — every
     * client until an admin unticks the box — keeps the historical behaviour.
     *
     * Presentation only. The API enforces the same flag on every write path;
     * `data.config` here is as fresh as the last session refresh, so a toggle
     * made mid-session shows up on the next one.
     */
    static clientAllowsSupplierCreate(data) {
        return data?.config?.suppliers?.allow_create !== false;
    }

    /** Whether the tenant may bulk-import suppliers from Xpens (XPEN-3267). */
    static clientAllowsSupplierImport(data) {
        return data?.config?.suppliers?.allow_import !== false;
    }

    static hasExpenseField(data, fieldName, defaultValue = true) {
        if (!this.userHasPageAccess(data, 'expenses')) {
            return defaultValue;
        }
        const mode = data?.config?.expenses_review?.mode;
        const disabledByMode = REVIEW_MODE_DISABLED_EXPENSE_FIELDS[mode];
        if (disabledByMode?.has(fieldName)) {
            return false;
        }
        if (fieldName === 'field_billable' && (mode === REVIEW_MODE_CLIENT_ONLY || mode === REVIEW_MODE_CLIENT_NON_CLIENT)) {
            return true;
        }
        if (fieldName === 'field_matter_split' && (mode === REVIEW_MODE_CLIENT_ONLY || mode === REVIEW_MODE_CLIENT_NON_CLIENT)) {
            return true;
        }
        const expensesModule = data.config.components.find(comp => comp.name === 'expenses');
        const hasFieldFlags = expensesModule?.options?.some(opt => opt.name.startsWith('field_'));
        if (!hasFieldFlags) {
            return defaultValue;
        }
        if (fieldName === FIELD_IMPUTATION_LEVEL1) {
            return !!expensesModule.options.find(
                opt => opt.name === FIELD_IMPUTATION_LEVEL1 || opt.name === RETIRED_FIELD_EXPENSE_CATEGORY
            );
        }
        return !!expensesModule.options.find(opt => opt.name === fieldName);
    }

    static getRequiredExpenseFields(data) {
        const requiredFields = data?.config?.expenses_review?.required_fields;
        if (!Array.isArray(requiredFields)) {
            return null;
        }
        return [...new Set(requiredFields
            .filter(fieldName => fieldName !== RETIRED_FIELD_IMPUTATION_SUBCATEGORY)
            .map(fieldName => (
                fieldName === RETIRED_FIELD_EXPENSE_CATEGORY ? FIELD_IMPUTATION_LEVEL1 : fieldName
            )))];
    }

    /**
     * Returns true when a visible review field should block the form if empty.
     *
     * `required_fields` is the new API-owned UI contract. When it is absent,
     * preserve the legacy frontend required behaviour until the backend patch
     * seeds explicit defaults for every client.
     */
    static isExpenseFieldRequired(data, fieldName, defaultValue = DEFAULT_REQUIRED_EXPENSE_FIELDS.has(fieldName)) {
        const visibilityDefault = !OPT_IN_EXPENSE_FIELDS.has(fieldName);
        if (!this.hasExpenseField(data, fieldName, visibilityDefault)) {
            return false;
        }
        const requiredFields = this.getRequiredExpenseFields(data);
        if (requiredFields == null) {
            return defaultValue;
        }
        return requiredFields.includes(fieldName);
    }

    /**
     * Order of the review form's main slots for this session (XPEN-3652).
     *
     * The saved `config.expenses_review.field_order` applies only in Custom mode
     * (strict `mode === 'custom'`, like the Custom gates in conditional-fields.js;
     * the API fills a missing mode with "custom" before sending the config) and
     * only for a user with the Expenses page, the same gate as hasExpenseField.
     * Otherwise, and when no order is saved, today's default order is returned.
     * @param {object} data MobX store
     * @returns {string[]} the 8 slot ids, each exactly once
     */
    static getExpenseFieldOrder(data) {
        const expensesReview = data?.config?.expenses_review;
        if (!this.userHasPageAccess(data, 'expenses') || expensesReview?.mode !== REVIEW_MODE_CUSTOM) {
            return resolveExpenseFieldOrder(null);
        }
        return resolveExpenseFieldOrder(expensesReview.field_order);
    }

    /**
     * Expense-account review only (caller passes showInvoiceNumber into MainInfo).
     * Hidden for Client-only and Client / Non-client presets; Custom uses field_invoice_number.
     */
    static shouldShowExpenseReviewInvoiceNumber(data) {
        // When expenses access cannot be determined, default to showing (safer than hiding).
        if (!this.userHasPageAccess(data, 'expenses')) {
            return true;
        }
        const mode = data?.config?.expenses_review?.mode;
        if (mode === REVIEW_MODE_CLIENT_ONLY || mode === REVIEW_MODE_CLIENT_NON_CLIENT) {
            return false;
        }
        return this.hasExpenseField(data, FIELD_INVOICE_NUMBER, true);
    }

    static userHasRole(data, roles) {
        return data != null  && roles.includes(data.role);
    }

    static paymentTermsOptions(data) {
        return [
            { label: `Net 7  ${data?.t?.read("day")}s`, value: 7 },
            { label: `Net 15 ${data?.t?.read("day")}s`, value: 15 },
            { label: `Net 30 ${data?.t?.read("day")}s`, value: 30 },
            { label: `${data?.t?.read("cash_on_delivery")}`, value: 0 },
        ];
    }

    static getEntityOptions(data) {
        return [{ label: data.t.read("supplier_table.organization"), value: "EntityOrg" }, { label: data.t.read("supplier_table.person"), value: "EntityPerson" }];
    }

    static getGroupOptions(data) {
        return [{ label: data.t.read("p-card"), value: "pcard" }];
    }

    static isExportFormat(data, exportFormat = "elite") {
        return data?.config?.invoice_exporter?.export_format === exportFormat;
    }

    static hasAderantIntegration(data) {
        const aderant = data?.config?.aderant;
        return aderant != null
            && typeof aderant === "object"
            && !Array.isArray(aderant)
            && Object.keys(aderant).length > 0;
    }

    /** Parse Aderant Rank Id from /client/options/ rows (int or numeric string). */
    static parseRankOptionId(raw) {
        if (typeof raw === "number" && Number.isInteger(raw) && raw > 0) {
            return raw;
        }
        if (typeof raw === "string" && /^\d+$/.test(raw.trim())) {
            const parsed = parseInt(raw.trim(), 10);
            return parsed > 0 ? parsed : null;
        }
        return null;
    }

    /** Map /client/options/?field_name=Rank rows to react-select options. */
    static mapRankOptionsFromApi(rows) {
        return (rows ?? [])
            .map((row) => {
                const id = Utils.parseRankOptionId(row?.Id);
                if (id == null) {
                    return null;
                }
                return { value: id, label: row?.Description || String(id) };
            })
            .filter(Boolean);
    }

    static isEngine(data, engine = "default") {
        return data?.config?.engine === engine;
    }

    static isEngineType(data, engineType = "default") {
        return data?.config?.type === engineType;
    }

    static userCanModify(data, user) {
        const roleHierarchy = {
            SuperAdmin: ["SuperAdmin", "Admin", "Manager", "Approver", "Clerk", "Assistant"],
            Admin: ["Admin", "Manager", "Approver", "Clerk", "Assistant"],
            Manager: ["Manager", "Approver", "Clerk", "Assistant"],
            Approver: ["Approver"],
            Clerk: ["Clerk"],
            Assistant: ["Assistant"]
        };
    
        return roleHierarchy[data.role].includes(user.role);
    }
    static styleSelect = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            boxShadow: state.isFocused ? "0px 0px 10px #F39800 " : "0 !important"
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menu: (base) => ({ ...base, zIndex: 9999 })
    };

    static customStyleSelect = {
        container: (base) => ({
          ...base,
          width: '100%',
          minWidth: 0,
          flex: '1 1 auto',
        }),
        control: (baseStyles, state) => ({
          ...baseStyles,
          display: 'flex',
          alignItems: 'center',
          border: 'none',
          boxShadow: state.isFocused ? '0px 0px 5px #F39800 ' : '0 !important',
          width: '100%',
          backgroundColor: 'transparent',
          minHeight: '26px',
          paddingTop: 0,
          paddingBottom: 0,
          fontSize: Utils.getSelectInputFontSize(),
        }),
      
        valueContainer: (base) => ({
          ...base,
          alignItems: 'center',
          padding: 0,
          minHeight: '26px',
        }),
      
        // The placeholder itself
        placeholder: (base) => ({
          ...base,
          paddingTop: 0,
          paddingBottom: 0,
          marginTop: 0,
          marginBottom: 0,
          display: 'flex',
          alignItems: 'center',
          height: '26px',
          lineHeight: '26px',
          fontSize: Utils.getSelectInputFontSize(),
        }),
      
        // The "hidden input" wrapper that was causing the misalignment
        inputContainer: (base) => ({
          ...base,
          padding: 0,
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          height: '26px',
        }),
      
        // The visible input text (when typing)
        input: (base) => ({
          ...base,
          lineHeight: '26px',
          padding: 0,
          // margin: 0,
          fontSize: Utils.getSelectInputFontSize(),
        }),
      
        // Selected value
        singleValue: (base) => ({
          ...base,
          lineHeight: '1.2',
          fontSize: Utils.getSelectInputFontSize(),
          whiteSpace: 'normal',
        }),
      
        indicatorsContainer: (base) => ({
          ...base,
          display: 'flex',
          alignItems: 'center',
          padding: 0,
          height: '26px',
        }),
      
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999,
        }),

        // XPEN-3467: react-select defaults the menu to 100% of the control, so
        // in a narrow table column long options wrap over several lines. Size to
        // content with the control's width as the floor.
        //
        // `minWidth: '100%'` resolves against the control either way: an inline
        // menu is positioned inside the select container, and a portalled one
        // sits in a `menuPortal` wrapper react-select already sets to the
        // control's width. The options keep their own wrapping, so a label past
        // the cap wraps rather than overflowing.
        menu: (base) => ({
          ...base,
          ...dropdownWidthStyle('100%'),
        }),
      };

    /**
     * Statuses that are approved for deletion
     */
    static DELETABLE_STATUSES = [
        "Pending",
        "Processing",
        "Need_Attention",
        "Completed",
    ];

    /**
     * Checks if all invoices in the selection have statuses that are approved for deletion
     * @param {Array} invoiceSelection - Array of invoice objects with a 'status' property
     * @returns {Boolean} - True if all invoices have deletable statuses, false if any invoice has a non-deletable status
     */
    static allInvoicesDeletable(invoiceSelection) {
        if (!invoiceSelection || invoiceSelection.length === 0) {
            return false;
        }

        // Check if all invoices have deletable statuses
        return invoiceSelection.every(x => this.DELETABLE_STATUSES.includes(x.status));
    }

    /**
     * Checks if all selected invoices belong to the current user
     * @param {Object} data - User data object with id_user, username, etc.
     * @param {Array} invoiceSelection - Array of invoice objects with user property
     * @returns {Boolean} - True if all invoices belong to the user, false otherwise
     */
    static allInvoicesBelongToUser(data, invoiceSelection) {
        if (!invoiceSelection || invoiceSelection.length === 0) {
            return false;
        }

        const currentUserId = data.id_user;
        return invoiceSelection.every(inv => {
            // Check by id_user if available
            if (inv.id_user) {
                return inv.id_user === currentUserId;
            }
        });
    }
      
}

export const getSelectStyles = (hasError, touched) => ({
    control: (baseStyles, state) => ({
        ...baseStyles,
        minHeight: "30px",
        height: "30px",
        boxShadow: state.isFocused ? "0px 0px 10px #F39800 " : "0 !important",
        borderColor: hasError && touched ? "#dc3545" : baseStyles.borderColor,
        borderWidth: hasError && touched ? "1px" : baseStyles.borderWidth,
        "&:hover": {
            borderColor: hasError && touched ? "#dc3545" : baseStyles["&:hover"]?.borderColor || baseStyles.borderColor
        }
    }),
    valueContainer: (base) => ({
        ...base,
        padding: "0 8px",
        display: "flex",
        alignItems: "center",
        lineHeight: "30px"
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        lineHeight: "30px"
    }),
    indicatorsContainer: (base) => ({
        ...base,
        padding: 0,
        height: "30px",
        display: "flex",
        alignItems: "center"
    }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: 4,
        display: "flex",
        alignItems: "center"
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 })
});


export const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

export const capitalizeFirstLetter = (string) => {
    if (typeof string !== 'string' || string.length === 0) {
        return ''; // Handle empty or non-string inputs
    }
    return string.charAt(0).toUpperCase() + string.slice(1);
}
